/* 213A · editable interface strings.
   The island is optional and every lookup carries the original literal as its
   fallback, so with no override the byte on screen is the byte P6-R4 shipped. */
const TOS_UI_STRINGS = (() => {
  try {
    const el = typeof document === "undefined" ? null : document.getElementById("tos-ui-strings");
    return el ? JSON.parse(el.textContent || "{}") : {};
  } catch (error) { return {}; }
})();
const TOS_UI = (key, fallback) => {
  const value = TOS_UI_STRINGS[key];
  return typeof value === "string" && value !== "" ? value : fallback;
};
const TOS_UI_T = (key, pattern, args) => TOS_UI(key, pattern)
  .replace(/\{(\d+)\}/g, (whole, index) => (args[Number(index)] === undefined ? whole : String(args[Number(index)])));
import { scheduleBoot } from "./3os-living-r1c-p1-boot-gate.js";
/* ===========================================================================
   site-shell.js · 206 — Sunlit Society Complete HTML Storefront
   ---------------------------------------------------------------------------
   THE ONE SHELL CONTROLLER. It runs on all 22 routes and owns exactly the
   surfaces that must behave identically everywhere:

     · mobile menu      — native <dialog>, focus trap, Escape, body lock,
                          focus return, close-on-navigate
     · cookie consent   — unset / necessary / all / preferences, persisted
     · live region      — one polite announcer for shell-level state changes
     · search           — Shopify owns submission and server results
     · FAQ              — open/closed plus real hash deep-linking
     · contact          — Shopify owns submission, errors and success
     · Society interest — the same grammar, with no promise attached

   It does NOT own the cart, the format selector, the loader, the films or the
   copy modes. `product-desire.js` remains the single cart + selection owner
   sitewide, `main.js` keeps the homepage loader/film/reveal, and `site-film.js`
   owns the single hero film on the four routes that are allowed one.

   Menu ownership was removed from main.js and product-desire.js in this fork
   precisely so there is one owner, not three.
   =========================================================================== */

import { CONSENT } from "./3os-living-r1c-site-content.js";

const $ = (selector, scope = document) => scope?.querySelector(selector) ?? null;
const $$ = (selector, scope = document) => [...(scope?.querySelectorAll(selector) ?? [])];

/* --------------------------------------------------------------------------
   268 · Vertical-only navigation

   `Element.scrollIntoView()` is allowed to scroll both axes and older Safari
   versions also treat an unsupported `overflow: clip` as visible. On a page
   with rotated editorial cards this can leave the fixed header in place while
   the content canvas is parked outside the viewport. Internal navigation is a
   vertical operation on this site, so calculate only Y and actively settle X.
   -------------------------------------------------------------------------- */
const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

function settleHorizontalAxis(target) {
  let node = target?.parentElement ?? null;
  while (node && node !== document.body) {
    if (node.scrollLeft !== 0) node.scrollLeft = 0;
    node = node.parentElement;
  }
  document.documentElement.scrollLeft = 0;
  document.body.scrollLeft = 0;
}

function instantWindowScroll(top) {
  const nodes = [document.documentElement, document.body];
  const previous = nodes.map((node) => ({
    node,
    value: node.style.getPropertyValue("scroll-behavior"),
    priority: node.style.getPropertyPriority("scroll-behavior"),
  }));
  nodes.forEach((node) => node.style.setProperty("scroll-behavior", "auto", "important"));
  window.scrollTo(0, top);
  window.requestAnimationFrame(() => previous.forEach(({ node, value, priority }) => {
    if (value) node.style.setProperty("scroll-behavior", value, priority);
    else node.style.removeProperty("scroll-behavior");
  }));
}

function verticalScrollTo(target, { block = "start", behavior } = {}) {
  if (!target?.getBoundingClientRect) return false;

  const rect = target.getBoundingClientRect();
  const viewportHeight = window.visualViewport?.height || window.innerHeight || document.documentElement.clientHeight;
  const header = $("[data-r2-header], [data-tos-header]");
  const headerHeight = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
  const availableHeight = Math.max(1, viewportHeight - headerHeight);
  const currentY = window.scrollY || window.pageYOffset || 0;
  let top = currentY + rect.top - headerHeight;

  /* A tall disclosure must expose its summary, not be centred with its first
     lines above the viewport. Short targets may still land in the calm centre. */
  if (block === "center" && rect.height <= availableHeight) {
    top = currentY + rect.top - headerHeight - ((availableHeight - rect.height) / 2);
  }

  const maxTop = Math.max(0, document.documentElement.scrollHeight - viewportHeight);
  top = Math.max(0, Math.min(Math.round(top), maxTop));
  /* A 5–11k px smooth journey is both disorienting and unreliable in WebKit;
     short local moves keep the polish, chapter jumps settle immediately. */
  const isLongJump = Math.abs(top - currentY) > viewportHeight * 1.5;
  const motion = behavior || (reducedMotion.matches || isLongJump ? "auto" : "smooth");

  settleHorizontalAxis(target);
  if (motion === "auto") {
    instantWindowScroll(top);
  } else try {
    window.scrollTo({ top, left: 0, behavior: motion });
  } catch {
    instantWindowScroll(top);
  }

  /* WebKit can recalculate a transformed/just-opened target one frame later.
     Reassert only the horizontal invariant; never fight the user's Y scroll. */
  window.requestAnimationFrame(() => settleHorizontalAxis(target));
  window.setTimeout(() => settleHorizontalAxis(target), motion === "smooth" ? 460 : 0);
  return true;
}

window.tosVerticalScroll = verticalScrollTo;

function setupInternalAnchors() {
  if (document.documentElement.dataset.tosVerticalAnchors === "true") return;
  document.documentElement.dataset.tosVerticalAnchors = "true";

  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const anchor = event.target?.closest?.("a[href]");
    if (!anchor || anchor.matches(".skip-link, [data-r2-proof-open], [data-proof-open], [data-r2-club-open]")) return;
    const href = anchor.getAttribute("href") || "";
    if (!href.startsWith("#") || href.length < 2) return;

    let id;
    try { id = decodeURIComponent(href.slice(1)); } catch { id = href.slice(1); }
    const target = document.getElementById(id);
    if (!target) return;

    event.preventDefault();
    if (target.tagName === "DETAILS") target.open = true;
    const url = new URL(window.location.href);
    url.hash = id;
    if (window.location.hash === `#${id}`) window.history.replaceState({}, "", url);
    else window.history.pushState({}, "", url);
    verticalScrollTo(target, { block: "start" });
  });
}

/* --------------------------------------------------------------------------
   Storage — guarded exactly like the cart's. A blocked, full or disabled
   localStorage must never break the shell; it only costs persistence.
   -------------------------------------------------------------------------- */
const storage = {
  ok: true,
  read(key) {
    if (!this.ok) return null;
    try {
      return window.localStorage.getItem(key);
    } catch {
      this.ok = false;
      return null;
    }
  },
  write(key, value) {
    if (!this.ok) return;
    try {
      window.localStorage.setItem(key, value);
    } catch {
      this.ok = false;
    }
  },
};

/* --------------------------------------------------------------------------
   One polite live region for the shell. The cart keeps its own; two regions
   with different owners are clearer than one region with two writers.
   -------------------------------------------------------------------------- */
function announce(message) {
  const region = $("[data-shell-live]");
  if (!region) return;
  /* Re-announce an identical string by clearing first — otherwise a repeated
     message (e.g. the same validation error twice) is silently dropped. */
  if (region.textContent === message) region.textContent = "";
  window.setTimeout(() => {
    region.textContent = message;
  }, 40);
}

/* --------------------------------------------------------------------------
   Generic dialog wiring. Native <dialog> supplies the focus trap and Escape;
   we add body lock, backdrop close and focus return.
   -------------------------------------------------------------------------- */
function wireDialog(dialog, { openers = [], closers = [] } = {}) {
  if (!dialog) return null;

  const open = (source) => {
    dialog._returnFocusTo = source ?? document.activeElement ?? null;
    if (!dialog.open && typeof dialog.showModal === "function") dialog.showModal();
    openers.forEach((button) => {
      if (button.hasAttribute("aria-expanded")) button.setAttribute("aria-expanded", dialog.open ? "true" : "false");
    });
    document.body.classList.add("is-locked");
  };

  const close = () => {
    if (dialog.open) dialog.close();
  };

  openers.forEach((button) => button.addEventListener("click", () => open(button)));
  closers.forEach((button) => button.addEventListener("click", close));

  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) close();
  });

  dialog.addEventListener("close", () => {
    openers.forEach((button) => {
      if (button.hasAttribute("aria-expanded")) button.setAttribute("aria-expanded", "false");
    });
    document.body.classList.remove("is-locked");
    const target = dialog._returnFocusTo;
    dialog._returnFocusTo = null;
    if (target?.isConnected) target.focus({ preventScroll: true });
  });

  return { open, close };
}

/* --------------------------------------------------------------------------
   1 · MOBILE MENU
   -------------------------------------------------------------------------- */
function setupMenu() {
  const dialog = $("[data-menu-dialog]");
  if (!dialog) return;
  /* Claim ownership so no other module can wire the same dialog twice. */
  if (dialog.dataset.shellWired === "true") return;
  dialog.dataset.shellWired = "true";

  const menu = wireDialog(dialog, {
    openers: $$("[data-menu-open]"),
    closers: $$("[data-menu-close]", dialog),
  });

  /* A same-page anchor must close the sheet; a real route navigation closes it
     by unloading. Closing both is harmless and keeps the body unlocked if the
     browser restores the page from bfcache. */
  $$("a[href]", dialog).forEach((link) => {
    link.addEventListener("click", () => menu?.close());
  });

  window.addEventListener("pageshow", () => {
    /* `is-locked` is shared by the menu, loader and modal surfaces. A fresh
       pageshow can fire after the illustrated loader has acquired the lock;
       releasing it here made the page scroll underneath the intro. */
    const anotherLockOwner = document.querySelector("[data-loader], dialog[open]");
    if (!dialog.open && !anotherLockOwner) document.body.classList.remove("is-locked");
  });
}

/* --------------------------------------------------------------------------
   2 · COOKIE CONSENT
   States: unset (banner) · necessary · all · preferences (dialog).
   Nothing is loaded on any choice — this build ships no analytics tool — and
   the copy says exactly that rather than implying a tracker was switched off.
   -------------------------------------------------------------------------- */
function readConsent() {
  const raw = storage.read(CONSENT.storageKey);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    if (parsed.version !== CONSENT.version) return null;
    return { analytics: parsed.analytics === true };
  } catch {
    return null;
  }
}

function writeConsent(analytics) {
  storage.write(
    CONSENT.storageKey,
    JSON.stringify({ version: CONSENT.version, analytics: analytics === true }),
  );
}

function setupConsent() {
  const banner = $("[data-consent]");
  const dialog = $("[data-consent-prefs]");
  const summary = $("[data-consent-summary]");
  const toggle = $("[data-consent-analytics]");

  const paintSummary = (state) => {
    if (!summary) return;
    summary.textContent =
      state === null
        ? TOS_UI("ui_nicio_alegere_salvata_inca_pe_acest", "Nicio alegere salvată încă pe acest dispozitiv.")
        : state.analytics
          ? TOS_UI("ui_alegerea_salvata_strict_necesare_si_anal", "Alegerea salvată: strict necesare și analiză.")
          : TOS_UI("ui_alegerea_salvata_doar_strict_necesare", "Alegerea salvată: doar strict necesare.");
  };

  const showBanner = (show) => {
    if (!banner) return;
    banner.dataset.open = String(show);
    document.body.classList.toggle("tos-consent-open", show);
  };

  const decide = (analytics, message) => {
    writeConsent(analytics);
    paintSummary({ analytics });
    if (toggle) toggle.checked = analytics;
    showBanner(false);
    if (dialog?.open) dialog.close();
    announce(message);
  };

  const stored = readConsent();
  paintSummary(stored);
  if (toggle) toggle.checked = stored?.analytics === true;
  showBanner(stored === null);

  $$("[data-consent-accept]").forEach((button) =>
    button.addEventListener("click", () =>
      decide(true, "Preferințe de cookies salvate: strict necesare și analiză."),
    ),
  );
  $$("[data-consent-necessary]").forEach((button) =>
    button.addEventListener("click", () =>
      decide(false, "Preferințe de cookies salvate: doar strict necesare."),
    ),
  );

  const prefs = wireDialog(dialog, {
    openers: $$("[data-consent-open]"),
    closers: $$("[data-consent-close]", dialog),
  });

  $("[data-consent-save]")?.addEventListener("click", () => {
    decide(
      toggle?.checked === true,
      toggle?.checked
        ? "Preferințe de cookies salvate: strict necesare și analiză."
        : "Preferințe de cookies salvate: doar strict necesare.",
    );
  });

  /* `cookies.html` links back into the preferences sheet, so a visitor can
     change the decision without clearing storage. */
  if (window.location.hash === "#preferinte" && prefs) prefs.open();
}

/* --------------------------------------------------------------------------
   3 · SEARCH — 208C2
   Shopify owns GET submission, result truth and no-JS behavior. Predictive
   enhancement lives in its route-scoped controller and never intercepts submit.
   -------------------------------------------------------------------------- */

/* --------------------------------------------------------------------------
   4 · FAQ — open/closed plus real deep-linking
   A question is a linkable thing: `faq.html#intrebare-livrare` opens it and
   scrolls to it, and opening one updates the address so it can be shared.
   -------------------------------------------------------------------------- */
function setupFaq() {
  const faq = $("[data-faq]");
  if (!faq) return;

  const items = $$("details[id]", faq);
  if (!items.length) return;

  const openFromHash = (hash, { focus = false } = {}) => {
    const id = (hash || "").replace(/^#/, "");
    if (!id) return;
    const target = items.find((item) => item.id === id);
    if (!target) return;
    target.open = true;
    verticalScrollTo(target, { block: "start" });
    if (focus) $("summary", target)?.focus({ preventScroll: true });
  };

  items.forEach((item) => {
    item.addEventListener("toggle", () => {
      if (!item.open) return;
      const url = new URL(window.location.href);
      url.hash = item.id;
      window.history.replaceState({}, "", url);
    });
  });

  openFromHash(window.location.hash);
  window.addEventListener("hashchange", () => openFromHash(window.location.hash, { focus: true }));
}

/* --------------------------------------------------------------------------
   5 · LOCAL FORMS — Contact and Society interest
   Nothing is transmitted. Validation is real, so the error state is a real
   state and not a decoration; the success state is explicitly labelled as a
   local review simulation next to the message, never inside it.

   `?state=loading|success|error` is a documented LOCAL REVIEW instrument: it
   paints a state for evidence capture without pretending a network happened.
   -------------------------------------------------------------------------- */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function paintStatus(status, state, title, body) {
  if (!status) return;
  status.dataset.state = state;
  const strong = $("b", status);
  const text = $("span", status);
  if (strong) strong.textContent = title;
  if (text) text.textContent = body;
}

function setupLocalForm(form) {
  const status = $("[data-form-status]", form);
  const submit = $("button[type='submit']", form);
  const messages = {
    loading: form.dataset.formLoading || TOS_UI("ui_se_trimite", "Se trimite…"),
    success: form.dataset.formSuccess || TOS_UI("ui_mesaj_pregatit_local", "Mesaj pregătit local."),
    successBody:
      form.dataset.formSuccessBody ||
      TOS_UI("ui_simulare_de_review_local_formularul_nu", "Simulare de review local: formularul nu transmite date. Canalul real se conectează la migrarea în Shopify."),
    error: form.dataset.formError || TOS_UI("ui_mai_lipseste_ceva", "Mai lipsește ceva."),
  };

  const fields = $$("[data-field]", form);

  const validateField = (field) => {
    const input = $("input, select, textarea", field);
    const error = $("[data-field-error]", field);
    if (!input) return true;

    let problem = "";
    const value = String(input.value ?? "").trim();

    if (input.required && input.type === "checkbox" && !input.checked) {
      problem = field.dataset.fieldRequired || TOS_UI("ui_aceasta_confirmare_este_necesara", "Această confirmare este necesară.");
    } else if (input.required && input.type !== "checkbox" && !value) {
      problem = field.dataset.fieldRequired || "Completează acest câmp.";
    } else if (input.type === "email" && value && !EMAIL_SHAPE.test(value)) {
      problem = TOS_UI("ui_adresa_de_email_nu_pare_completa", "Adresa de email nu pare completă.");
    }

    if (error) error.textContent = problem;
    if (input.type === "checkbox") input.setAttribute("aria-invalid", String(Boolean(problem)));
    else input.setAttribute("aria-invalid", String(Boolean(problem)));
    return !problem;
  };

  fields.forEach((field) => {
    const input = $("input, select, textarea", field);
    input?.addEventListener("blur", () => {
      if (input.getAttribute("aria-invalid") === "true") validateField(field);
    });
    input?.addEventListener("input", () => {
      if (input.getAttribute("aria-invalid") === "true") validateField(field);
    });
  });

  form.addEventListener("submit", (event) => {
    event.preventDefault();

    const results = fields.map(validateField);
    if (results.includes(false)) {
      const firstBad = fields[results.indexOf(false)];
      paintStatus(
        status,
        "error",
        messages.error,
        "Câmpurile marcate au nevoie de o corectură înainte de trimitere.",
      );
      announce(TOS_UI_T("ui_0_campurile_marcate_au_nevoie_de", `${messages.error} Câmpurile marcate au nevoie de o corectură.`, [messages.error]));
      $("input, select, textarea", firstBad)?.focus({ preventScroll: false });
      return;
    }

    paintStatus(status, "loading", messages.loading, "Se pregătește mesajul.");
    announce(messages.loading);
    if (submit) submit.dataset.busy = "true";

    window.setTimeout(() => {
      if (submit) submit.dataset.busy = "false";
      paintStatus(status, "success", messages.success, messages.successBody);
      announce(`${messages.success} ${messages.successBody}`);
      form.reset();
      fields.forEach((field) => {
        $("[data-field-error]", field) && ($("[data-field-error]", field).textContent = "");
        $("input, select, textarea", field)?.setAttribute("aria-invalid", "false");
      });
    }, 700);
  });

  /* Local review override for evidence capture. */
  const forced = new URLSearchParams(window.location.search).get("state");
  if (!forced) return;
  if (forced === "loading") {
    paintStatus(status, "loading", messages.loading, "Se pregătește mesajul.");
    if (submit) submit.dataset.busy = "true";
  }
  if (forced === "success") paintStatus(status, "success", messages.success, messages.successBody);
  if (forced === "error") {
    paintStatus(
      status,
      "error",
      messages.error,
      "Câmpurile marcate au nevoie de o corectură înainte de trimitere.",
    );
    fields
      .filter((field) => $("input, select, textarea", field)?.required)
      .forEach((field) => {
        const input = $("input, select, textarea", field);
        const error = $("[data-field-error]", field);
        input?.setAttribute("aria-invalid", "true");
        if (error) error.textContent = field.dataset.fieldRequired || "Completează acest câmp.";
      });
  }
}

function setupForms() {
  $$("[data-local-form]").forEach(setupLocalForm);
}

/* --------------------------------------------------------------------------
   Boot
   -------------------------------------------------------------------------- */
function boot() {
  document.documentElement.classList.add("shell-js");
  setupMenu();
  setupConsent();
  setupInternalAnchors();
  setupFaq();
  setupForms();
}

/* R1C-M1-P1 — boot after first paint, own macrotask, same order */
scheduleBoot(boot);
